import { Form } from "@unform/web";
import { PlusCircle, Trash, UserStar } from "lucide-react";
import React, {
  createContext,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import Input from "~/components/RegisterForm/Input";
import RegisterFormMenu from "~/components/RegisterForm/Menu";
import api from "~/services/api";
import { toast, Zoom } from "react-toastify";
import CheckboxInput from "~/components/RegisterForm/CheckboxInput";
import InputLine from "~/components/RegisterForm/InputLine";
import InputLineGroup from "~/components/RegisterForm/InputLineGroup";
import FormHeader from "~/components/RegisterForm/FormHeader";
import Preview from "~/components/Preview";
import { getRegistries } from "~/functions";
import FormLoading from "~/components/RegisterForm/FormLoading";
import { FullGridContext } from "../..";
import SelectPopover from "~/components/RegisterForm/SelectPopover";
import { surveyQuestionTypeOptions } from "~/functions/selectPopoverOptions";
import { Scope } from "@unform/core";

export const InputContext = createContext({});

export default function PagePreview({
  access,
  id,
  defaultFormType = "preview",
  endpoint = "surveys-questions",
}) {
  const {
    handleOpened,
    setOpened,
    successfullyUpdated,
    setSuccessfullyUpdated,
  } = useContext(FullGridContext);
  const [pageData, setPageData] = useState({
    options: [{ id: null, description: null, value: null }],
    has_options: false,
    is_active: true,
    options_edittable: true,
    description: "",
    type: "",
  });
  const [formType, setFormType] = useState(defaultFormType);
  const [fullscreen, setFullscreen] = useState(false);
  const [activeMenu, setActiveMenu] = useState("general");
  const [registry, setRegistry] = useState({
    created_by: null,
    created_at: null,
    updated_by: null,
    updated_at: null,
    canceled_by: null,
    canceled_at: null,
  });
  const generalForm = useRef();
  async function getPageData() {
    if (id !== "new") {
      try {
        const { data } = await api.get(`/${endpoint}/${id}`);
        setPageData({
          ...pageData,
          ...data,
          has_options: data.options.length > 0,
          options_edittable: surveyQuestionTypeOptions.find(
            (type) => type.value === data.type
          )?.options_edittable,
          loaded: true,
        });
        const {
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        } = data;
        const registries = await getRegistries({
          created_by,
          created_at,
          updated_by,
          updated_at,
          canceled_by,
          canceled_at,
        });
        setRegistry(registries);
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    } else {
      setPageData({ ...pageData, loaded: true });
      setFormType("full");
    }
  }
  useEffect(() => {
    getPageData();
  }, []);

  async function handleGeneralFormSubmit(data) {
    if (pageData.has_options === true && pageData.options.length === 0) {
      toast("At least one option is required!", {
        autoClose: 1000,
        type: "error",
        transition: Zoom,
      });
      return;
    }
    if (successfullyUpdated) {
      toast("No need to be saved!", {
        autoClose: 1000,
        type: "info",
        transition: Zoom,
      });
      return;
    }
   
    if (Array.isArray(data.options)) {
      data.options = data.options.map((option) => ({
        ...option,
        is_other: option.is_other === "true" || option.is_other === true,
      }));
    }

    if (id === "new") {
      try {
        delete data.id;
        await api.post(`/${endpoint}`, {
          ...data,
        });
        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
        handleOpened(null);
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    } else if (id !== "new") {
      try {
        await api.put(`/${endpoint}/${id}`, {
          ...data,
        });
        setSuccessfullyUpdated(true);
        toast("Saved!", { autoClose: 1000 });
        handleOpened(null);
      } catch (err) {
        if (err?.response?.data?.error) {
          toast(err.response.data.error, { type: "error", autoClose: 3000 });
        }
      }
    }
  }

  function handleCloseForm() {
    if (!successfullyUpdated) {
      toast("Changes discarted!", { autoClose: 1000 });
    }
    handleOpened(null);
  }

  function handleAddOption() {
    setPageData({
      ...pageData,
      options: [
        ...pageData.options,
        { id: null, description: null, value: null },
      ],
    });
  }

  function handleRemoveOption(index) {
    setPageData({
      ...pageData,
      options: pageData.options.filter((option, i) => i !== index),
    });
  }

  return (
    <Preview formType={formType} fullscreen={fullscreen}>
      {pageData ? (
        <div className="flex h-full flex-row items-start justify-between gap-4">
          <div className="flex flex-col items-center justify-between text-xs w-40 gap-4">
            <RegisterFormMenu
              setActiveMenu={setActiveMenu}
              activeMenu={activeMenu}
              name="general"
            >
              <UserStar size={16} /> Surveys
            </RegisterFormMenu>
          </div>
          <div className="border h-full rounded-xl overflow-hidden flex flex-1 flex-col justify-start">
            <div className="flex flex-col items-start justify-start text-sm overflow-y-scroll">
              <Form
                ref={generalForm}
                onSubmit={handleGeneralFormSubmit}
                className="w-full"
              >
                <InputContext.Provider
                  value={{
                    id,
                    generalForm,
                    setSuccessfullyUpdated,
                    fullscreen,
                    setFullscreen,
                    successfullyUpdated,
                    handleCloseForm,
                    handleInactivate: () => null,
                    canceled: pageData.canceled_at,
                  }}
                >
                  {pageData.loaded ? (
                    <>
                      <FormHeader
                        access={access}
                        title={`Survey Questions`}
                        registry={registry}
                        InputContext={InputContext}
                      />
                      <InputLineGroup
                        title="general"
                        activeMenu={activeMenu === "general"}
                      >
                        <InputLine title="Question">
                          <Input
                            type="text"
                            name="description"
                            required
                            grow
                            readOnly={!pageData.is_active}
                            title="Description"
                            defaultValue={pageData.description}
                            InputContext={InputContext}
                          />
                          <SelectPopover
                            name="type"
                            required
                            grow
                            title="Type"
                            isSearchable
                            readOnly={!pageData.is_active}
                            defaultValue={surveyQuestionTypeOptions.find(
                              (opt) => opt.value === pageData.type
                            )}
                            options={surveyQuestionTypeOptions}
                            setReturnPopover={(type) =>
                              setPageData({
                                ...pageData,
                                has_options: type.has_options,
                                options: type.defaultOptions,
                                options_edittable: type.options_edittable,
                              })
                            }
                            InputContext={InputContext}
                          />
                          <Input
                            type="number"
                            name="sort_order"
                            grow
                            readOnly={!pageData.is_active}
                            title="Order"
                            defaultValue={pageData.sort_order ?? 0}
                            InputContext={InputContext}
                          />
                        </InputLine>
                        <InputLine title="Instruction">
                          <Input
                            type="text"
                            name="instruction"
                            grow
                            readOnly={!pageData.is_active}
                            title="Instruction (shown below the question)"
                            defaultValue={pageData.instruction ?? ""}
                            InputContext={InputContext}
                          />
                        </InputLine>
                        {pageData.has_options ? (
                          <>
                            {pageData.options?.map((option, index) => {
                              return (
                                <Scope key={index} path={`options[${index}]`}>
                                  <InputLine
                                    title={index === 0 ? "Options" : ""}
                                  >
                                    <Input
                                      type="hidden"
                                      name="id"
                                      defaultValue={option.id}
                                      InputContext={InputContext}
                                    />
                                    {pageData.is_active &&
                                      pageData.options_edittable &&
                                      index > 0 &&
                                      index === pageData.options.length - 1 && (
                                        <button
                                          type="button"
                                          onClick={() =>
                                            handleRemoveOption(index)
                                          }
                                        >
                                          <Trash size={14} className="mt-4" />
                                        </button>
                                      )}
                                    <Input
                                      type="text"
                                      name="description"
                                      grow
                                      title="Description"
                                      readOnly={
                                        !pageData.is_active ||
                                        !pageData.options_edittable
                                      }
                                      required
                                      defaultValue={option.option_description}
                                      InputContext={InputContext}
                                    />
                                    <Input
                                      type="hidden"
                                      name="value"
                                      grow
                                      title="Value"
                                      readOnly={
                                        !pageData.is_active ||
                                        !pageData.options_edittable
                                      }
                                      required
                                      defaultValue={option.option_value}
                                      InputContext={InputContext}
                                    />
                                    {pageData.options_edittable && (
                                      <CheckboxInput
                                        name="is_other"
                                        title="Other"
                                        shrink
                                        readOnly={!pageData.is_active}
                                        defaultValue={
                                          option.is_other ? "true" : ""
                                        }
                                        InputContext={InputContext}
                                      />
                                    )}
                                  </InputLine>
                                </Scope>
                              );
                            })}
                            {pageData.is_active &&
                              pageData.options_edittable && (
                                <button
                                  type="button"
                                  onClick={() => handleAddOption()}
                                  className="bg-slate-100 border ml-6 py-2 px-2 text-xs flex flex-row justify-center items-center gap-2 rounded-md transition-all hover:border-primary hover:text-primary"
                                >
                                  <PlusCircle size={16} /> Add Option
                                </button>
                              )}
                          </>
                        ) : null}
                      </InputLineGroup>
                    </>
                  ) : (
                    <FormLoading />
                  )}
                </InputContext.Provider>
              </Form>
            </div>
          </div>
        </div>
      ) : null}
    </Preview>
  );
}
